/**
 * authSessionLookupBound — the per-caller time bound on authSession's shared session lookup.
 *
 * Split out of authSession.ts (file-size ceiling) with the code unchanged: the named bound, the
 * monotonic deadline, and the once-per-lookup report. authSession owns the in-flight slot and tells
 * this module when a new lookup starts; every caller's wait on that lookup is then bounded here.
 */

import { captureError } from '../utils/scoutErrorCapture';

/** When the lookup in the slot started, on the MONOTONIC clock — every waiter's bound is measured from here. */
let inFlightStartedAt = 0;

/**
 * Milliseconds on a clock that only moves forward (Codex P2 #2215). The shared bound must not be measured
 * on the wall clock: a device clock correction during a lookup (NTP, a time-zone or manual change) would
 * either end a healthy wait at once or stretch a hung one far past the bound. Same guard as serviceFetch's
 * own latency measurement.
 */
function monotonicNow(): number {
  return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
}
/** Whether the lookup in the slot has already been reported as over its bound (one report per lookup). */
let inFlightReported = false;

/**
 * How long ONE caller waits for the session lookup before it gives up on the bearer header.
 *
 * WHY (TASK-FE-BEARER-FALLBACK-GETSESSION-BOUNDED-2026-10-09): `serviceFetch` awaits this lookup while
 * it builds the headers — BEFORE the caller's AbortSignal / request timeout is wired. supabase-js's
 * `getSession()` / `refreshSession()` can fail to settle at all (a stuck storage lock, a hung refresh),
 * and then every request whose stored token is stale or absent never left the device. The bearer is only
 * the FALLBACK (the HttpOnly cookie stays primary), so past the bound the request goes cookie-only and the
 * server's response decides. 5 s: well above a healthy refresh round-trip on a field connection, well
 * below the point a rep takes the app for frozen.
 */
export const BEARER_LOOKUP_TIMEOUT_MS = 5_000;

/** What a caller is rejected with when the lookup outlives the bound; applyBearerFallback's catch absorbs it. */
export class BearerLookupTimeoutError extends Error {
  constructor() {
    super(`session lookup for the bearer fallback exceeded its ${BEARER_LOOKUP_TIMEOUT_MS}ms bound`);
    this.name = 'BearerLookupTimeoutError';
  }
}

type SentryCapture = (error: unknown, context?: unknown) => void;

/**
 * Report an over-bound lookup on BOTH repo paths, neither of which may throw into the request.
 *
 * The Sentry bridge comes first because it needs NO session (Codex P2 #2215): `captureError` writes to
 * scout_activity_log with the stored Supabase token and drops the event when there is none — which is
 * exactly the state a hung lookup is most likely to be in (no token, or an expired one). Same bridge
 * access + guards as failedWriteCapture / reportQueryError: this lib cannot import @sentry/react, so it
 * uses the `window.__sentryCapture` hook main.tsx installs.
 */
function reportLookupTimeout(err: BearerLookupTimeoutError): void {
  try {
    if (typeof window !== 'undefined') {
      const capture = (window as unknown as { __sentryCapture?: SentryCapture }).__sentryCapture;
      if (typeof capture === 'function') {
        capture(err, { tags: { source: 'bearer-fallback', errorName: err.name }, level: 'warning' });
      }
    }
  } catch {
    // Reporting must never hold or fail the request it reports on.
  }
  captureError(err, { tags: { feature: 'bearer-fallback' }, level: 'warning' });
}

/** The rejection for an over-bound lookup, reported ONCE per lookup however many callers wait behind it. */
function lookupTimedOut(): BearerLookupTimeoutError {
  const err = new BearerLookupTimeoutError();
  if (!inFlightReported) {
    inFlightReported = true;
    reportLookupTimeout(err);
  }
  return err;
}

/** A NEW lookup takes the slot: its bound starts now and it has not been reported. */
export function startLookupBound(): void {
  inFlightStartedAt = monotonicNow();
  inFlightReported = false;
}

/**
 * `lookup`, or a BearerLookupTimeoutError once what is left of its bound has passed. Ends this caller's
 * WAIT only: supabase-js takes no AbortSignal, so the lookup itself runs on and keeps the slot until it
 * settles. A caller that arrives after the bound is rejected at once — it never waits again on a lookup
 * already known to be stuck.
 */
export function withinLookupBound(lookup: Promise<string | null>): Promise<string | null> {
  const remainingMs = inFlightStartedAt + BEARER_LOOKUP_TIMEOUT_MS - monotonicNow();
  if (remainingMs <= 0) return Promise.reject(lookupTimedOut());
  return new Promise<string | null>((resolve, reject) => {
    const timer = setTimeout(() => reject(lookupTimedOut()), remainingMs);
    lookup.then(
      (token) => { clearTimeout(timer); resolve(token); },
      (err: unknown) => { clearTimeout(timer); reject(err); },
    );
  });
}
